import { useMemo } from "react";

import {
    BsMetaUtil,
    getLoopAtCapacity,
    getLoopBorrowCapDetails,
    isLeverageLoop,
    isLoopComingSoon,
    LoopExpanded,
    LoopTokenPriceChange,
    OracleOHLC,
    OracleQuote,
    useLoopOraclePriceQuotes,
    useLoopTokenPriceChanges,
    useOracleHistoricalPrices
} from "@bridgesplit/abf-react";
import { StatProps, StatRow, StatColumn, MEDIA, useAppPalette, Text } from "@bridgesplit/ui";
import {
    filterUndefined,
    formatMultiplier,
    formatPercent,
    formatUsd,
    nearestHour,
    isDefined,
    TIME
} from "@bridgesplit/utils";
import { useMediaQuery } from "@mui/material";
import { COPY } from "app/constants";
import { StatWithSubtext, TokenStat, TokenUsdStat } from "app/components/common";

import { useLoopContext } from "../LoopContext";

export function LoopVaultStats() {
    const { loopExpanded } = useLoopContext();
    const { data: priceData } = useOracleHistoricalPrices({
        mints: loopExpanded?.collateralToken ? [loopExpanded.collateralToken.mint] : [],
        startTimestamp: nearestHour() - TIME.DAY * 7
    });
    const displayAsColumn = useMediaQuery(MEDIA.MD.below);

    if (isLoopComingSoon(loopExpanded)) {
        return <ComingSoonLoopVaultStats displayAsColumn={displayAsColumn} />;
    }

    if (isLeverageLoop(loopExpanded)) {
        const historicalPrices =
            priceData && loopExpanded?.collateralToken.mint ? priceData[loopExpanded.collateralToken.mint] : undefined;
        return (
            <LeverageLoopVaultStats
                loopExpanded={loopExpanded}
                displayAsColumn={displayAsColumn}
                historicalPrices={historicalPrices}
            />
        );
    }
    return <YieldLoopVaultStats loopExpanded={loopExpanded} displayAsColumn={displayAsColumn} />;
}

function LeverageLoopVaultStats({
    loopExpanded,
    historicalPrices,
    displayAsColumn
}: {
    loopExpanded: LoopExpanded | undefined;
    historicalPrices: OracleOHLC[] | undefined;
    displayAsColumn: boolean;
}) {
    const {
        data: [priceChange7d],
        loading: priceChangeLoading
    } = useLoopTokenPriceChanges(!loopExpanded ? undefined : loopExpanded?.collateralToken?.mint, historicalPrices);

    const { principalOracle, collateralOracle } = useLoopOraclePriceQuotes(loopExpanded);

    const leverageLoopStats = getLeverageLoopStats(loopExpanded, displayAsColumn, priceChange7d) ?? [];
    const baseStats = getBaseStats(loopExpanded, displayAsColumn, principalOracle, collateralOracle) ?? [];
    const stats: StatProps[] = [...leverageLoopStats, ...baseStats].filter(filterUndefined);

    const isLoading = priceChangeLoading || !loopExpanded;

    if (displayAsColumn) {
        return <StatColumn loading={isLoading} stats={stats} />;
    }

    return <StatRow loading={isLoading} stats={stats} />;
}

function YieldLoopVaultStats({
    loopExpanded,
    displayAsColumn
}: {
    loopExpanded: LoopExpanded | undefined;
    displayAsColumn: boolean;
}) {
    const { principalOracle, collateralOracle } = useLoopOraclePriceQuotes(loopExpanded);

    const yieldLoopStats = getYieldLoopStats(loopExpanded, displayAsColumn, principalOracle) ?? [];
    const baseStats = getBaseStats(loopExpanded, displayAsColumn, principalOracle, collateralOracle) ?? [];
    const stats: StatProps[] = [...yieldLoopStats, ...baseStats].filter(filterUndefined);

    const isLoading = !loopExpanded;

    if (displayAsColumn) {
        return <StatColumn loading={isLoading} stats={stats} />;
    }

    return <StatRow loading={isLoading} stats={stats} />;
}

function getBaseStats(
    loopExpanded: LoopExpanded | undefined,
    displayAsColumn: boolean,
    principalOracle: OracleQuote | undefined,
    collateralOracle: OracleQuote | undefined
): StatProps[] {
    const borrowCapWarning = getLoopBorrowCapDetails(loopExpanded);

    const availableLiquidity = Math.min(
        loopExpanded?.loopVault?.principalAmountAvailable ?? 0,
        borrowCapWarning?.remainingGlobalCapacity ?? 0
    );

    const totalDeployedUsd = loopExpanded?.loopVault.totalPrincipalDeployedUsd ?? 0;
    const totalCollateralDepositedUsd = loopExpanded?.loopVault.collateralDepositedUsd ?? 0;
    const totalMarketSizeUsd = totalDeployedUsd + totalCollateralDepositedUsd;

    return [
        {
            value: (
                <StatWithSubtext
                    stat={formatMultiplier(loopExpanded?.loopVault.maxLeverage)}
                    subtext={`Avg: ${formatMultiplier(loopExpanded?.loopVault.wAvgLeverage)}`}
                    displayAsColumn={displayAsColumn}
                />
            ),
            caption: COPY.MAX_LEVERAGE_TERM
        },
        {
            value: (
                <TokenStat
                    tokenAmount={availableLiquidity}
                    token={loopExpanded?.principalToken}
                    usdPrice={principalOracle?.usdPrice ?? 0}
                    displayAsColumn={displayAsColumn}
                    warning={borrowCapWarning?.warningMessage ?? undefined}
                />
            ),
            caption: COPY.AVAILABLE_LIQUIDITY_TERM
        },
        {
            value: (
                <TokenUsdStat
                    usdValue={loopExpanded?.loopVault.collateralDepositedUsd ?? 0}
                    token={loopExpanded?.collateralToken}
                    usdPrice={collateralOracle?.usdPrice ?? 0}
                    displayAsColumn={displayAsColumn}
                />
            ),
            caption: COPY.DEPOSITED_TERM,
            tooltip: `The total amount of ${BsMetaUtil.getSymbol(
                loopExpanded?.collateralToken
            )} used as collateral across Loops`
        },
        {
            value: (
                <TokenUsdStat
                    usdValue={totalMarketSizeUsd}
                    token={loopExpanded?.collateralToken}
                    usdPrice={collateralOracle?.usdPrice ?? 0}
                    displayAsColumn={displayAsColumn}
                />
            ),
            caption: "Market size"
        }
    ];
}

function getLeverageLoopStats(
    loopExpanded: LoopExpanded | undefined,
    displayAsColumn: boolean,
    priceChange7d: LoopTokenPriceChange | undefined
): StatProps[] | undefined {
    const change = priceChange7d?.change;
    const currentPrice = priceChange7d?.end;

    return [
        {
            value: (
                <StatWithSubtext
                    stat={<PriceChange change={change} />}
                    subtext={`Price: ${formatUsd(currentPrice)}`}
                    displayAsColumn={displayAsColumn}
                />
            ),
            caption: `${BsMetaUtil.getSymbol(loopExpanded?.collateralToken)} 7D Change`
        }
    ];
}

function PriceChange({ change }: { change: number | undefined }) {
    const { textDisabled, error, success } = useAppPalette();

    const color = useMemo(() => {
        if (isDefined(change)) {
            return change > 0 ? success : error;
        }
        return textDisabled;
    }, [change, success, error, textDisabled]);

    return (
        <Text
            variant="h3"
            sx={{
                gap: 0.5,
                color
            }}
        >
            {formatPercent(change, { includePlus: true })}
        </Text>
    );
}

function ComingSoonLoopVaultStats({ displayAsColumn }: { displayAsColumn: boolean }) {
    const placeholder = <Text color="disabled">—</Text>;
    const stats: StatProps[] = [
        { value: placeholder, caption: "Avg. APY" },
        { value: placeholder, caption: COPY.MAX_LEVERAGE_TERM },
        { value: placeholder, caption: COPY.AVAILABLE_LIQUIDITY_TERM },
        { value: placeholder, caption: COPY.DEPOSITED_TERM },
        { value: placeholder, caption: "Market size" }
    ];

    if (displayAsColumn) {
        return <StatColumn stats={stats} />;
    }
    return <StatRow stats={stats} />;
}

function getYieldLoopStats(
    loopExpanded: LoopExpanded | undefined,
    displayAsColumn: boolean,
    principalOracle: OracleQuote | undefined
): StatProps[] | undefined {
    const isCapacity = getLoopAtCapacity(loopExpanded, principalOracle);

    if (isCapacity) {
        // Flip the stats: show Avg APY at the top, Max net APY as subtext.
        // Since max capacity APY will always be 0% when a vault is at capacity, we can pass in undefined to show a --, instead of 0%.
        return [
            {
                value: (
                    <StatWithSubtext
                        stat={formatPercent(loopExpanded?.loopVault.wAvgApy)}
                        subtext={`Max: ${formatPercent(undefined, { formatUndefinedAsNA: true })}`}
                        displayAsColumn={displayAsColumn}
                    />
                ),
                caption: "Avg. APY"
            }
        ];
    }

    return [
        {
            value: (
                <StatWithSubtext
                    stat={formatPercent(loopExpanded?.loopVault.maxLeveragedApyPct, { collapseNegatives: true })}
                    subtext={`Avg: ${formatPercent(loopExpanded?.loopVault.wAvgApy)}`}
                    displayAsColumn={displayAsColumn}
                />
            ),
            caption: "Max net APY",
            tooltip: `The APY for a new position opened at the best currently available rate.`
        }
    ];
}
